import {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
  ReactNode,
} from "react";
import { workOrdersService } from "@/services";
import type { ServiceOrderDto } from "@shared/types/ServiceOrderDto";

interface ServiceOrdersContextType {
  serviceOrders: ServiceOrderDto[];
  isLoading: boolean;
  error: string | null;
  fetchServiceOrders: () => Promise<void>;
  getServiceOrderById: (id: string) => ServiceOrderDto | undefined;
  updateProjectName: (
    serviceOrderId: string,
    customProjectName: string
  ) => Promise<ServiceOrderDto>;
}

const ServiceOrdersContext = createContext<ServiceOrdersContextType | null>(
  null,
);

export function ServiceOrdersProvider({ children }: { children: ReactNode }) {
  const [serviceOrders, setServiceOrders] = useState<ServiceOrderDto[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchServiceOrders = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const data = await workOrdersService.getServiceOrders();
      setServiceOrders(data);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Failed to fetch service orders",
      );
    } finally {
      setIsLoading(false);
    }
  }, []);

  const getServiceOrderById = useCallback(
    (id: string) => {
      return serviceOrders.find((so) => so.id === id);
    },
    [serviceOrders],
  );

  const updateProjectName = useCallback(
    async (serviceOrderId: string, customProjectName: string) => {
      const updatedOrder = await workOrdersService.updateProjectName(
        serviceOrderId,
        customProjectName
      );
      setServiceOrders((prev) =>
        prev.map((so) => (so.id === serviceOrderId ? updatedOrder : so))
      );
      return updatedOrder;
    },
    []
  );

  useEffect(() => {
    fetchServiceOrders();
  }, [fetchServiceOrders]);

  return (
    <ServiceOrdersContext.Provider
      value={{
        serviceOrders,
        isLoading,
        error,
        fetchServiceOrders,
        getServiceOrderById,
        updateProjectName,
      }}
    >
      {children}
    </ServiceOrdersContext.Provider>
  );
}

export function useServiceOrders() {
  const context = useContext(ServiceOrdersContext);
  if (!context) {
    throw new Error(
      "useServiceOrders must be used within a ServiceOrdersProvider",
    );
  }
  return context;
}
